import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';
import { Breadcrumb } from '@/components/jurisprudence/Breadcrumb';

const inputCls = "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

// `value` est envoyé tel quel à l'API (liste validée côté backend, en français) ;
// le libellé affiché est traduit via contact.subjects.<key>.
const SUBJECTS = [
  { key: 'general',     value: 'Question générale' },
  { key: 'technical',   value: 'Problème technique' },
  { key: 'sales',       value: 'Demande commerciale / cabinet' },
  { key: 'partnership', value: 'Partenariat' },
  { key: 'press',       value: 'Presse / média' },
  { key: 'other',       value: 'Autre' },
];

type Status = 'idle' | 'sending' | 'success' | 'error';

export default function PublicContact() {
  const { t } = useTranslation();
  const [name,    setName]    = useState('');
  const [email,   setEmail]   = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status,  setStatus]  = useState<Status>('idle');
  const [error,   setError]   = useState('');

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = name.trim() && emailValid && subject && message.trim().length >= 10 && status !== 'sending';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!emailValid) { setError(t('contact.errors.invalid_email')); return; }
    if (!canSubmit) return;
    setStatus('sending');
    setError('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email: email.trim(), subject, message }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // FastAPI 422 → detail est un tableau [{msg, loc, ...}]
        const detail = data?.detail;
        const msg = Array.isArray(detail)
          ? detail.map((d: any) => d.msg?.replace('Value error, ', '')).join(', ')
          : (typeof detail === 'string' ? detail : t('contact.errors.server'));
        throw new Error(msg);
      }
      setStatus('success');
    } catch (err: any) {
      setStatus('error');
      setError(err.message || t('contact.errors.generic'));
    }
  }

  return (
    <>
      <Helmet>
        <title>{t('contact.meta_title')}</title>
        <meta name="description" content={t('contact.meta_description')} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <Breadcrumb items={[
          { label: t('menu.home'), to: '/' },
          { label: t('nav.contact') },
        ]} />

        <main className="flex-1">

        {/* Hero */}
        <section className="border-b bg-muted/30 py-14 sm:py-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: 'var(--blue-l)', border: '1px solid var(--blue-m)',
              borderRadius: 20, padding: '5px 14px', marginBottom: 20,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--blue)', display: 'inline-block' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--blue)', letterSpacing: '.05em' }}>{t('contact.badge')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight" style={{ margin: '0 0 14px' }}>
              {t('contact.hero.title_start')}<br />
              <span className="text-primary">{t('contact.hero.title_highlight')}</span>
            </h1>
            <p style={{ fontSize: 16, color: 'var(--muted2)', margin: 0, lineHeight: 1.7 }}>
              {t('contact.hero.desc')}{' '}
              <a href="mailto:contact@smartlawyer.ai" style={{ color: 'var(--blue)', textDecoration: 'none', fontWeight: 600 }}>
                contact@smartlawyer.ai
              </a>
            </p>
          </div>
        </section>

        {/* Form */}
        <section className="py-12 pb-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">

            {status === 'success' ? (
              <div style={{
                background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 16,
                padding: '40px 40px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink2)', margin: '0 0 10px' }}>
                  {t('contact.success.title')}
                </h2>
                <p style={{ fontSize: 15, color: 'var(--muted2)', margin: '0 0 24px', lineHeight: 1.7 }}>
                  <Trans
                    i18nKey="contact.success.desc"
                    values={{ email }}
                    components={{ strong: <strong /> }}
                  />
                </p>
                <button
                  onClick={() => { setStatus('idle'); setName(''); setEmail(''); setSubject(''); setMessage(''); }}
                  className="px-7 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors cursor-pointer border-none"
                >
                  {t('contact.success.another')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="rounded-xl border bg-card p-8 sm:p-10">

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 24px', marginBottom: 20 }}>
                  {/* Nom */}
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink2)', marginBottom: 7 }}>
                      {t('contact.form.name')} <span style={{ color: 'var(--blue)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder={t('contact.form.name_placeholder')}
                      required
                      className={inputCls}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink2)', marginBottom: 7 }}>
                      {t('contact.form.email')} <span style={{ color: 'var(--blue)' }}>*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder={t('contact.form.email_placeholder')}
                      required
                      className={inputCls}
                    />
                  </div>
                </div>

                {/* Sujet */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink2)', marginBottom: 7 }}>
                    {t('contact.form.subject')} <span style={{ color: 'var(--blue)' }}>*</span>
                  </label>
                  <select
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    required
                    className={inputCls}
                    style={{ color: subject ? undefined : 'var(--muted-foreground)' }}
                  >
                    <option value="" disabled>{t('contact.form.subject_placeholder')}</option>
                    {SUBJECTS.map(s => (
                      <option key={s.key} value={s.value}>{t(`contact.subjects.${s.key}`)}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div style={{ marginBottom: 28 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink2)', marginBottom: 7 }}>
                    {t('contact.form.message')} <span style={{ color: 'var(--blue)' }}>*</span>
                  </label>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder={t('contact.form.message_placeholder')}
                    rows={6}
                    required
                    className={`${inputCls} min-h-[140px] resize-y leading-relaxed h-auto py-2.5`}
                  />
                  <div style={{ fontSize: 11, color: 'var(--muted3)', marginTop: 5, textAlign: 'right' }}>
                    {t('contact.form.char_count', { n: message.length, max: 5000 })}
                  </div>
                </div>

                {/* Error */}
                {status === 'error' && (
                  <div style={{
                    background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 10,
                    padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#c53030',
                  }}>
                    {error}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`w-full rounded-xl py-3.5 text-[15px] font-bold transition-all border-none ${
                    canSubmit
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer'
                      : 'bg-muted text-muted-foreground cursor-not-allowed'
                  }`}
                >
                  {status === 'sending' ? t('contact.form.sending') : t('contact.form.submit')}
                </button>

                <p style={{ fontSize: 12, color: 'var(--muted3)', textAlign: 'center', margin: '14px 0 0' }}>
                  {t('contact.form.privacy')}
                </p>
              </form>
            )}

            {/* Infos complémentaires */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 28 }}>
              {[
                { icon: '✉️', title: t('contact.info.email_title'), desc: 'contact@smartlawyer.ai', href: 'mailto:contact@smartlawyer.ai' },
                { icon: '⏱️', title: t('contact.info.delay_title'), desc: t('contact.info.delay_desc'), href: null },
              ].map(item => (
                <div key={item.title} className="rounded-xl border bg-card p-5 flex gap-4 items-start">
                  <span style={{ fontSize: 20 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink2)', marginBottom: 3 }}>{item.title}</div>
                    {item.href
                      ? <a href={item.href} style={{ fontSize: 13, color: 'var(--blue)', textDecoration: 'none' }}>{item.desc}</a>
                      : <div style={{ fontSize: 13, color: 'var(--muted2)' }}>{item.desc}</div>
                    }
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>
        </main>
      </div>

      <PublicFooter />
    </>
  );
}
